import React, { useState, useEffect } from "react";
import { firestore } from "../firebaseConfig";
import { Timestamp } from "firebase/firestore";
import moment from "moment";
import { ChevronDown, Clock, Calendar, Car, Bike, SquareParking, Info, Camera } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './Login';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import CameraPage from './CameraPage';

const primaryColor = '#33AE54';
const primaryColorLight = '#e6f4eb';

const styles = {
  container: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${primaryColorLight} 0%, #ffffff 100%)`,
    paddingBottom: '120px',
    position: 'relative',
    overflow: 'hidden',
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.1,
    background: `
      radial-gradient(circle at 0% 0%, ${primaryColor} 0%, transparent 50%),
      radial-gradient(circle at 100% 0%, ${primaryColor} 0%, transparent 50%),
      radial-gradient(circle at 100% 100%, ${primaryColor} 0%, transparent 50%),
      radial-gradient(circle at 0% 100%, ${primaryColor} 0%, transparent 50%)
    `,
  },
  main: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '10px',
    boxSizing: 'border-box',
    position: 'relative',
    zIndex: 1,
  },
  card: {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '24px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    padding: '1rem',
    marginTop: '1rem',
    backdropFilter: 'blur(10px)',
    animation: 'slideUp 0.5s ease-out',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '0.5rem',
    position: 'relative',
  },
  image: {
    width: '100%',
    maxWidth: '300px',
    height: 'auto',
    animation: 'float 6s ease-in-out infinite',
  },
  glow: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    animation: 'pulse 4s ease-in-out infinite',
  },
  header: {
    color: '#2d3748',
    fontSize: 'clamp(1.8rem, 5vw, 2.8rem)',
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: '1rem',
    marginTop: '0rem',
    background: `linear-gradient(135deg, ${primaryColor} 0%, #2d8a47 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subheader: {
    color: '#4a5568',
    fontSize: 'clamp(1rem, 2vw, 1.2rem)',
    textAlign: 'center',
    marginBottom: '2.5rem',
    opacity: 0.8,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    maxWidth: '500px',
    margin: '0 auto',
    position: 'relative',
  },
  input: {
    padding: '1rem 1.5rem',
    fontSize: '1.1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    outline: 'none',
    background: 'white',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    '&:focus': {
      borderColor: primaryColor,
      boxShadow: `0 0 0 3px ${primaryColorLight}`,
    },
  },
  button: {
    background: primaryColor,
    color: 'white',
    padding: '1rem 2rem',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(51, 174, 84, 0.2)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 8px rgba(51, 174, 84, 0.3)',
      background: '#2d8a47',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
  },
  error: {
    color: '#e53e3e',
    textAlign: 'center',
    marginTop: '1rem',
    padding: '0.75rem',
    background: 'rgba(229, 62, 62, 0.1)',
    borderRadius: '8px',
    animation: 'shake 0.5s ease-in-out',
  },
  accordion: {
    marginTop: '1rem',
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  },
  accordionHeader: {
    padding: '1.5rem',
    background: primaryColorLight,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      background: '#d4ebdc',
    },
  },
  accordionTitle: {
    margin: 0,
    fontSize: '1.2rem',
    fontWeight: '600',
    color: primaryColor,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  accordionContent: {
    padding: '1.5rem',
    background: 'white',
  },
  bookingCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '1.2rem',
    marginBottom: '1rem',
    border: `1px solid ${primaryColorLight}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.8rem',
  },
  bookingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#4a5568',
    fontSize: '0.95rem',
  },
  footer: {
    background: 'white',
    padding: '1.5rem',
    position: 'fixed',
    bottom: '0',
    width: '100%',
    boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.1)',
    zIndex: 1,
  },
  footerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 2rem',
  },
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  footerRight: {
    display: 'flex',
    gap: '2rem',
  },
  footerLink: {
    color: '#4a5568',
    textDecoration: 'none',
    fontSize: '0.95rem',
    transition: 'color 0.3s ease',
    '&:hover': {
      color: primaryColor,
    },
  },
  chevron: {
    transition: 'transform 0.3s ease',
  },
  '@keyframes slideUp': {
    from: {
      transform: 'translateY(20px)',
      opacity: 0,
    },
    to: {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      transform: 'translate(-50%, -50%) scale(1)',
      opacity: 0.2,
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.1)',
      opacity: 0.3,
    },
  },
  '@media (max-width: 768px)': {
    card: {
      padding: '1.5rem',
    },
    footerContent: {
      flexDirection: 'column',
      gap: '1rem',
      textAlign: 'center',
    },
    footerRight: {
      flexDirection: 'column',
      gap: '0.5rem',
    },
  },
evidenceButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    color: 'white',
    padding: '0.85rem 1.2rem',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1.5rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxShadow: '0 4px 10px rgba(220, 38, 38, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(220, 38, 38, 0.4)',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
  },
  
  evidenceForm: {
    marginTop: '1.5rem',
    padding: '1.5rem',
    background: 'white',
    borderRadius: '16px',
    border: '1px dashed #dc2626',
    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
    animation: 'slideUp 0.4s ease-out',
  },
  
  fileUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    borderRadius: '12px',
    border: '2px dashed #e2e8f0',
    background: '#f8fafc',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '1rem',
    '&:hover': {
      borderColor: '#dc2626',
      background: '#fef2f2',
    },
  },
  
  fileInputLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#4a5568',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center',
  },
  
  fileInput: {
    display: 'none',
  },
  
  evidenceImagePreview: {
    width: '100%',
    maxWidth: '300px',
    marginTop: '1rem',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    border: '3px solid white',
  },
  
  submitEvidenceButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    color: 'white',
    padding: '0.85rem 1.2rem',
    borderRadius: '12px',
    border: 'none',
    width: '100%',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginTop: '1.5rem',
    boxShadow: '0 4px 10px rgba(220, 38, 38, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(220, 38, 38, 0.4)',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
  },
  
  cancelButton: {
    background: 'transparent',
    color: '#4a5568',
    padding: '0.85rem 1.2rem',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '0.75rem',
    width: '100%',
    transition: 'all 0.2s ease',
    '&:hover': {
      background: '#f1f5f9',
    },
  },
  
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginTop: '1rem',
  },

  cameraButton: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    padding: '0.85rem 1.2rem',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  }
};


function Home() {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({});
  const [showEvidenceForm, setShowEvidenceForm] = useState({});
  const [evidenceImage, setEvidenceImage] = useState({});
  const [docId, setDocId] = useState("");
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [todayScanCount, setTodayScanCount] = useState({
    total: 0,
    notFound: 0,
    valid: 0,
  });
  const [totalScanCount, setTotalScanCount] = useState({
    total: 0,
    notFound: 0,
    valid: 0,
  });

  useEffect(() => {
    const loggedInUser = localStorage.getItem('user');
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    
    if (loggedInUser && loginTimestamp) {
      const TWO_HOURS = 2 * 60 * 60 * 1000; 
      const isExpired = Date.now() - parseInt(loginTimestamp) > TWO_HOURS;
      
      if (isExpired) {
        localStorage.removeItem('user');
        localStorage.removeItem('loginTimestamp');
        setUser(null);
      } else {
        setUser(JSON.parse(CryptoJS.AES.decrypt(loggedInUser, 'ZEHkZJo6taM52FSiio1h4g==').toString(CryptoJS.enc.Utf8)));
      }
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (user?.employeeId) {
      getTotalCount();
    }
  }, [user]);
  if (!authChecked) {
    return null;
  }
  if (showCamera) {
    return <CameraPage onCapture={(imageData) => {
      setEvidenceImage(imageData);
      setShowEvidenceForm(prev => ({
        ...prev,
        [docId]:true
      }));
    }} onClose={() => setShowCamera(false)} />;
  }

  const notify = (message) => {
    toast(message, {
      position: "bottom-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
    });
  };

  const toggleAccordion = (index) => {
    setOpenAccordions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleSearch = async () => {
    setError("");
    setResult(null);
    setIsLoading(true);
    setOpenAccordions({});

    if (!vehicleNumber.trim()) {
      setError("Please enter a vehicle number");
      setIsLoading(false);
      return;
    }

    try {
      const yesterday = Timestamp.fromDate(
        moment(moment().subtract(1, "days").format("DD-MM-YYYY hh:mm A"), "DD-MM-YYYY hh:mm A").toDate()
      );
      
      const ref = firestore
        .collection("booked_parking_order")
        .where("userVehicle.vehicleNumber", "==", vehicleNumber)
        .where("bookingEndTime", ">", yesterday);
        // .where("bookingStartTime", ">", yesterday);

      const snapshots = await ref.get();
      var data_log = {
        "vehicleNumber" : vehicleNumber,
        "searchDate": moment().format("DD-MM-YYYY hh:mm A"),
        "searchAt": Timestamp.now(),
        "searchResultStatus": snapshots.empty ? "No vehicle found" : "Vehicle found",
        "searchResult": [],
        "officer": user,
        "location": await new Promise((resolve, reject) => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              position => resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              }),
              error => resolve("Location access denied")
            );
          } else {
            resolve("Geolocation not supported");
          }
        }),
      };
      if (snapshots.empty) {
        setResult("No vehicle found");
      } else {
        const data = snapshots.docs.map((doc) => doc.data());
        data_log.searchResult = data;
        setResult(data);
        // Initialize all accordions as open
        const initialAccordionState = {};
        data.forEach((_, index) => {
          initialAccordionState[index] = true;
        });
        setOpenAccordions(initialAccordionState);
      }
      const docRef = await firestore.collection("enforcement_search_log").add(data_log);
      setDocId(docRef.id);
      getTotalCount();
      // firestore.collection("search_log").get().then((snapshots) => {
      //   snapshots.docs.forEach((doc) => {
      //     doc.ref.delete();
      //   });
      // });
    } catch (err) {
      setDocId("");
      setError(`Error fetching data: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEvidenceSubmit = async (index, booking) => {
    try {
      const image = evidenceImage[index];
      if (!image) {
        setError("Please upload an image");
        return;
      }

      await firestore.collection("enforcement_search_log").doc(docId).update({
        "evidence": image
      });
      
      setShowEvidenceForm(prev => ({...prev, [index]: false}));
      setEvidenceImage(prev => ({...prev, [index]: null}));
      notify("Evidence submitted successfully");
    } catch (err) {
      notify(`Error submitting evidence: ${err.message}`);
      setError(`Error submitting evidence: ${err.message}`);
    }
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
          setEvidenceImage(prev => ({
            ...prev,
            [index]: compressedDataUrl
          }));
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('loginTimestamp');
    setUser(null);
  };

  const getTotalCount = async () => {
    try {
      const startOfDay = Timestamp.fromDate(
        moment().startOf("day").toDate()
      );

      const endOfDay = Timestamp.fromDate(
        moment().endOf("day").toDate()
      );

      const snapshot = await firestore
        .collection("enforcement_search_log")
        .where("officer.employeeId", "==", user.employeeId)
        .where("searchAt", ">=", startOfDay)
        .where("searchAt", "<=", endOfDay)
        .get();

      const snapshotTotal = await firestore
        .collection("enforcement_search_log")
        .where("officer.employeeId", "==", user.employeeId)
        .get();

      const notFound = snapshot.docs.filter(
        doc => doc.data().searchResultStatus === "No vehicle found"
      ).length;

      const valid = snapshot.docs.filter(
        doc => doc.data().searchResultStatus === "Vehicle found"
      ).length;

      setTodayScanCount({
        total: snapshot.size,
        notFound,
        valid,
      });

      const notFoundTotal = snapshotTotal.docs.filter(
        doc => doc.data().searchResultStatus === "No vehicle found"
      ).length;

      const validTotal = snapshotTotal.docs.filter(
        doc => doc.data().searchResultStatus === "Vehicle found"
      ).length;

      setTotalScanCount({
        total: snapshotTotal.size,
        notFound: notFoundTotal,
        valid: validTotal,
      });

    } catch (err) {
      console.error("Error getting today count:", err);
    }
  };


  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern} />
      <main style={styles.main}>
        <div style={styles.card}>

          <div style={styles.imageContainer}>
            <div style={styles.glow} />
            <img 
              src="../assets/img/car-insurance.png"
              alt="Parking Enforcement"
              style={styles.image}
            />
          </div>

          <h1 style={styles.header}>Enforcement Check</h1>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.6rem',
            marginBottom: '1rem',
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '12px',
              padding: '0.8rem 0.5rem',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
              border: `2px solid ${primaryColorLight}`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}>
              <h3 style={{ 
                margin: '0 0 0.3rem 0', 
                color: '#64748b', 
                fontSize: '0.7rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>Scanned</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
              }}>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '800', 
                  color: primaryColor,
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Today</span><br/>
                  {todayScanCount.total}
                </p>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '800',
                  color: '#64748b',
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Total</span><br/>
                  {totalScanCount.total}
                </p>
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '12px',
              padding: '0.8rem 0.5rem',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(22,163,74,0.15)',
              border: '2px solid #bbf7d0',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}>
              <h3 style={{ 
                margin: '0 0 0.3rem 0', 
                color: '#15803d', 
                fontSize: '0.7rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>Valid</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
              }}>
                <p style={{ 
                  fontSize: '1.2rem', 
                  fontWeight: '800', 
                  color: '#16a34a',
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Today</span><br/>
                  {todayScanCount.valid}
                </p>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '800',
                  color: '#64748b',
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Total</span><br/>
                  {totalScanCount.valid}
                </p>
              </div>  
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              borderRadius: '12px',
              padding: '0.8rem 0.5rem',
              textAlign: 'center',
              boxShadow: '0 4px 15px rgba(220,38,38,0.15)',
              border: '2px solid #fecaca',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            }}>
              <h3 style={{ 
                margin: '0 0 0.3rem 0', 
                color: '#b91c1c', 
                fontSize: '0.7rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>Not Found</h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
              }}>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '800', 
                  color: '#dc2626',
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Today</span><br/>
                  {todayScanCount.notFound}
                </p>
                <p style={{ 
                  fontSize: '1rem', 
                  fontWeight: '800',
                  color: '#64748b',
                  margin: 0,
                  lineHeight: 1,
                  flex:1
                }}>
                  <span style={{
                    fontSize: '0.7rem',
                    fontWeight: '600',
                  }}>Total</span><br/>
                  {totalScanCount.notFound}
                </p>
              </div>  
            </div>
          </div>
          {/* <p style={styles.subheader}>Quick and easy vehicle verification system</p> */}

          <div style={styles.form}>
            <input
              type="text"
              placeholder="Enter vehicle number"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
              style={styles.input}
              maxLength={10}
            />
            <button
              onClick={handleSearch}
              disabled={isLoading}
              style={{
                ...styles.button,
                opacity: isLoading ? 0.7 : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Checking...' : 'Verify Vehicle'}
            </button>
            <button
              onClick={() => setShowCamera(true)}
              style={styles.cameraButton}
            >
              <Camera size={18}/>
              Open Camera
            </button>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          {result && (
            <div style={{ marginTop: '2rem' }}>
              {typeof result === 'string' ? (
                <p style={{ textAlign: 'center', color: '#4a5568', fontSize: '1.1rem' }}>{result}</p>
              ) : (
                result.map((item, index) => (
                  <div key={index} style={styles.accordion}>
                    <div 
                      style={styles.accordionHeader}
                      onClick={() => toggleAccordion(index)}
                    >
                      <h3 style={styles.accordionTitle}>
                        {item.vehicleType === "4 Wheeler" ? <Car size={20} /> : <Bike size={20} />}
                        Booking #{index + 1}
                      </h3>
                      <ChevronDown 
                        size={20} 
                        style={{
                          ...styles.chevron,
                          transform: openAccordions[index] ? 'rotate(180deg)' : 'rotate(0)',
                        }}
                      />
                    </div>
                    {openAccordions[index] && (
                      <div style={styles.accordionContent}>
                        <div style={styles.bookingInfo}>
                          {item.vehicleType === "4 Wheeler" ? <Car size={18} /> : <Bike size={18} />}
                          <strong>Booking Type:</strong> {item.bookingType}
                        </div>
                        <div style={styles.bookingInfo}>
                          <SquareParking size={18} />    
                          <strong>Parking Name:</strong> {item.parkingDetails?.name}
                        </div>
                        <div style={styles.bookingInfo}>
                          <Calendar size={18} />
                          <strong>Start Time:</strong> 
                          {moment(item.bookingStartTime.toDate()).format("DD-MM-YYYY hh:mm A")}
                        </div>
                        <div style={styles.bookingInfo}>
                          <Clock size={18} />
                          <strong>End Time:</strong>
                          {moment(item.bookingEndTime.toDate()).format("DD-MM-YYYY hh:mm A")}
                        </div>
                        <div style={styles.bookingInfo}>
                          <Info size={18} />
                          <strong>Status:</strong>
                          <span style={{
                            color: moment().isBetween(
                              item.bookingStartTime.toDate(),
                              item.bookingEndTime.toDate()
                            ) ? '#16a34a' : '#dc2626',
                            fontWeight: 'bold'
                          }}>
                            {moment().isBetween(
                              item.bookingStartTime.toDate(),
                              item.bookingEndTime.toDate()
                            ) ? 'Valid' : 'Expired'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
          
{docId && (
  <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
    <button
      style={styles.evidenceButton}
      onClick={() => setShowEvidenceForm(prev => ({...prev, 'no_vehicle': !prev.no_vehicle}))}
    >
      <Camera size={18} /> Submit Vehicle Evidence
    </button>
    
    {showEvidenceForm.no_vehicle && (
      <div style={styles.evidenceForm}>
        <h3 style={{margin: '0 0 1rem 0', color: '#dc2626', fontSize: '1.1rem', textAlign: 'center'}}>
          Upload Violation Evidence
        </h3>
        
        <div style={styles.fileUploadContainer}>
          <label style={styles.fileInputLabel}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload('no_vehicle', e)}
              style={styles.fileInput}
              id="evidence-upload"
            />
            {!evidenceImage?.no_vehicle ? (
              <>
                <Camera size={36} color="#dc2626" />
                <span>Click to upload evidence photo</span>
                <span style={{fontSize: '0.8rem', opacity: 0.7}}>JPG, PNG or HEIC formats</span>
              </>
            ) : (
              <>
                <img
                  src={evidenceImage.no_vehicle}
                  alt="Evidence preview"
                  style={styles.evidenceImagePreview}
                />
                <span style={{fontSize: '0.9rem', marginTop: '0.5rem'}}>Click to change image</span>
              </>
            )}
          </label>
        </div>
        
        <div style={styles.buttonGroup}>
          <button
            style={styles.submitEvidenceButton}
            onClick={() => handleEvidenceSubmit('no_vehicle')}
            disabled={!evidenceImage?.no_vehicle}
          >
            <Camera size={16} /> Submit Evidence
          </button>
          
          <button
            style={styles.cancelButton}
            onClick={() => {
              setShowEvidenceForm(prev => ({...prev, 'no_vehicle': false}));
              setEvidenceImage(prev => ({...prev, 'no_vehicle': null}));
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>
)}
        </div>
      </main>

      <ToastContainer />
    </div>
  );
}

export default Home;